/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Chip, CircularProgress, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { ChevronLeft } from '@material-ui/icons';
import queryString from 'query-string';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import TemplateMaterials from './TemplateMaterials';
import EditFields from '../../components/common/EditFields';
import TagsTextField from '../../components/common/TagsTextField';
import { resourcePrivacy, templatePrivacyFilterList } from '../../models/Resource';
import TemplateModel from '../../models/Template';
import { deepCopy } from '../../utils/lodashUtils';
import useBlocRefHook from '../../utils/useBlocRef.hook';

const useStyle = makeStyles((theme) => {
  return {
    buttonAction: {
      margin: '4px',
      paddingLeft: '20px',
      paddingRight: '20px',
    },
    buttonsContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'start',
      marginTop: '12px',
      paddingBottom: '12px',
    },

    fieldsContainer: {
      maxWidth: '500px',
      paddingRight: '8px',
      width: '100%',
    },

    [theme.breakpoints.up('lg')]: {
      materialsContainer: {
        borderLeft: '1px solid #999',
        paddingLeft: '24px',
      },
    },

    headerDescription: {
      color: theme.palette.grey['500'],
      fontSize: '12px',
    },

    linkBack: {
      alignItems: 'center',
      color: theme.palette.primary.main,
      display: 'flex',
      flexWrap: 'nowrap',
      fontSize: '14px',
      fontWeight: '600',
      justifyContent: 'start',
      textDecoration: 'none',
    },

    linkBackIcon: {
      marginRight: '0px',
    },
    loadingContainer: {
      padding: '60px 8px',
    },

    materialHeader: {
      color: '#172b4d',
      fontSize: '20px',
      fontWeight: '600',
    },

    tagContainer: {
      background: '#E7E7E7',
      borderRadius: '10px',
      display: 'inline-block',
      margin: '5px',
      padding: '5px 10px',
      textAlign: 'center',
    },
    tagName: {
      color: theme.palette.primary.main,
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '14px',
    },
  };
});

export default () => {
  const classes = useStyle();
  const blocRef = useBlocRefHook('templateDetailsBloc');

  const location = useLocation();
  const match = useRouteMatch({ location, path: '/collections/:id' });
  const history = useHistory();

  const { search } = useLocation();
  const { materialIds, token } = queryString.parse(search);

  const [template, setTemplate] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [userWorkspace, setUserWorkspace] = useState({});
  const [userDetails, setUserDetails] = useState({});
  const [redirectTarget, setRedirectTarget] = useState('');

  const privacyField = useRef();
  const generalField = useRef();

  const handleAddEducational = useCallback(
    (opts = {}) => {
      blocRef.current.openAddEducationalMaterials(opts);
    },
    [blocRef]
  );

  const handleAddSupplementary = useCallback(
    (opts = {}) => {
      blocRef.current.openAddSupplementaryMaterials(opts);
    },
    [blocRef]
  );

  const handleOpenResource = useCallback(
    (material) => {
      blocRef.current.openViewer({ ...material, ...material?.resource });
    },
    [blocRef]
  );

  const isNewTemplate = useMemo(() => {
    return location.pathname.includes('collection-new');
  }, [location]);

  const isPublicTemplate = useMemo(() => {
    return location.pathname.includes('public-template-new');
  }, [location]);

  const isAllowedToEdit = useMemo(() => {
    if (!template?.id) return true;

    if (template?.privacy === resourcePrivacy.GLOBAL && !userWorkspace?.isMediphanyWorkspace) {
      return false;
    }

    if (template?.privacy === resourcePrivacy.WORKSPACE && template?.workspaceId !== userWorkspace?.id) {
      return false;
    }

    return true;
  }, [userDetails, userWorkspace, template]);

  const managedPrivacyItems = useMemo(() => {
    const cloneList = [...templatePrivacyFilterList];

    if (!isAllowedToEdit) {
      return cloneList;
    }

    if (userWorkspace?.isMediphanyWorkspace) {
      return cloneList.filter((item) => item.value !== 'workspace');
    }

    if (!userWorkspace?.isMediphanyWorkspace) {
      return cloneList.filter((item) => item.value !== 'global');
    }

    return cloneList;
  }, [userWorkspace, isAllowedToEdit]);

  const generalFields = useMemo(
    () => [
      {
        disabled: !isAllowedToEdit || isLoading,
        label: 'Collection Name',
        name: 'title',
        onChange: (value) => {
          blocRef.current.changeTemplate({ title: value });
        },
        placeholder: 'Resource Title',
        required: true,
        type: 'text',
      },
      {
        className: 'custom-height',
        disabled: !isAllowedToEdit || isLoading,
        label: 'Collection Description',
        multiline: true,
        name: 'description',
        onChange: (value) => {
          blocRef.current.changeTemplate({ description: value });
        },
        placeholder: 'Resource Description',
        required: false,
        rows: 6,
        rowsMax: 8,
        type: 'text',
      },
    ],
    [isAllowedToEdit, isLoading]
  );

  const privacyFields = useMemo(
    () => [
      {
        disabled: !isAllowedToEdit || isLoading || isTagsLoading,
        items: managedPrivacyItems,
        label: 'Privacy',
        name: 'privacy',
        onChange: (value) => {
          blocRef.current.changeTemplate({ privacy: value.privacy });
        },
        required: true,
        type: 'select',
      },
    ],
    [managedPrivacyItems, isAllowedToEdit, isLoading, isTagsLoading]
  );

  const handleAddTag = useCallback(
    (value) => {
      const cloneTemplate = deepCopy(template);

      if (!cloneTemplate.tags) {
        cloneTemplate.tags = [];
      }

      const trimmed = value.trim();
      if (!(cloneTemplate.tags || []).includes(trimmed)) {
        cloneTemplate.tags.push(trimmed);
      }

      blocRef.current.changeTemplate({ tags: cloneTemplate.tags });
    },
    [template]
  );

  const handleDeleteTemplate = useCallback(() => {
    if (!template?.id) return;
    blocRef.current.deleteTemplate(template.id);
  }, [template?.id, blocRef]);

  const handleCancelTemplate = useCallback(() => {
    if (!isPublicTemplate) {
      history.push('/collections');
    }

    // FOR DESKTOP APP
    // eslint-disable-next-line no-console
    console.log('create-template:close');
  }, [isPublicTemplate, history]);

  const handleSaveTemplate = useCallback(async () => {
    const data = await Promise.all([generalField.current.getValidData(), privacyField.current.getValidData()]);

    if (data.some((d) => !d)) {
      blocRef.current.handleEmitError('Some fields are invalid, please double check again.');
      return;
    }

    if (!template?.educationalMaterials?.length && !template?.supplementaryMaterials?.length) {
      blocRef.current.handleEmitError('Please add at least one educational or supplementary material.');
      return;
    }

    const payload = {
      ...template,
      description: data[0].description,
      id: undefined,
      privacy: data[1].privacy,
      title: data[0].title,
    };

    if (template?.id) {
      blocRef.current.updateTemplate(template?.id, payload);
      return;
    }

    blocRef.current.createTemplate(payload);
  }, [template]);

  const handleRemoveTag = useCallback((index) => {
    setTemplate((prev) => {
      const cloneTemplate = deepCopy(prev);

      if (!cloneTemplate.tags) {
        cloneTemplate.tags = [];
      }

      cloneTemplate.tags.splice(index, 1);
      return cloneTemplate;
    });
  }, []);

  const handleRemoveEducational = useCallback(
    (id) => {
      let educationalMaterials = deepCopy(template?.educationalMaterials || []);
      educationalMaterials = educationalMaterials.filter((item) => item.id !== id);

      blocRef.current.changeTemplate({ educationalMaterials });
      blocRef.current.sortMaterialsOrder();
    },
    [template]
  );

  const handleRemoveSupplementary = useCallback(
    (id) => {
      let supplementaryMaterials = deepCopy(template?.supplementaryMaterials || []);
      supplementaryMaterials = supplementaryMaterials.filter((item) => item.id !== id);

      blocRef.current.changeTemplate({ supplementaryMaterials });
      blocRef.current.sortMaterialsOrder();
    },
    [template]
  );

  const handleSortEducational = useCallback(
    (sortEnd) => {
      if (sortEnd?.newIndex === sortEnd?.oldIndex) return;

      let educationalMaterials = deepCopy(template?.educationalMaterials || []);

      const [removed] = educationalMaterials.splice(sortEnd.oldIndex, 1);
      educationalMaterials.splice(sortEnd.newIndex, 0, removed);

      educationalMaterials = educationalMaterials.map((item, index) => ({ ...item, order: index + 1 }));

      blocRef.current.changeTemplate({ educationalMaterials });
      blocRef.current.sortMaterialsOrder();
    },
    [template?.educationalMaterials]
  );

  const handleSortSupplementary = useCallback(
    (sortEnd) => {
      if (sortEnd?.newIndex === sortEnd?.oldIndex) return;

      let supplementaryMaterials = deepCopy(template?.supplementaryMaterials || []);

      const [removed] = supplementaryMaterials.splice(sortEnd.oldIndex, 1);
      supplementaryMaterials.splice(sortEnd.newIndex, 0, removed);

      supplementaryMaterials = supplementaryMaterials.map((item, index) => ({ ...item, order: index + 1 }));

      blocRef.current.changeTemplate({ supplementaryMaterials });
      blocRef.current.sortMaterialsOrder();
    },
    [template?.supplementaryMaterials]
  );

  const renderTagsField = () => (
    <Grid container>
      <Grid item xs={12}>
        <Typography align="left" className="field-label">
          Resource Tags
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TagsTextField onAdd={handleAddTag} options={tags} />
      </Grid>
    </Grid>
  );

  useEffect(() => {
    const templateSub = blocRef.current.template$.subscribe(setTemplate);
    const isLoadingSub = blocRef.current.isLoading$.subscribe(setIsLoading);
    const isTagsLoadingSub = blocRef.current.isTagsLoading$.subscribe(setIsTagsLoading);
    const tagsSub = blocRef.current.tags$.subscribe(setTags);
    const userWorkspaceSub = blocRef.current.userWorkspace$.subscribe(setUserWorkspace);
    const userDetailsSub = blocRef.current.userDetails$.subscribe(setUserDetails);
    const redirectTargetSub = blocRef.current.redirectTarget$.subscribe(setRedirectTarget);

    return () => {
      templateSub.unsubscribe();
      isLoadingSub.unsubscribe();
      isTagsLoadingSub.unsubscribe();
      tagsSub.unsubscribe();
      userWorkspaceSub.unsubscribe();
      userDetailsSub.unsubscribe();
      redirectTargetSub.unsubscribe();

      blocRef.current.dispose();
    };
  }, []);

  useEffect(() => {
    if (materialIds?.length && materialIds?.split(',')?.length > 0) {
      blocRef.current.processMaterialIds(materialIds.split(','));
    }
  }, [blocRef, materialIds]);

  useEffect(() => {
    if (match?.params?.id) {
      blocRef.current.getTemplateById(match.params?.id);
    }
  }, [match?.params?.id]);

  useEffect(() => {
    if (redirectTarget && !isPublicTemplate) {
      history.push(redirectTarget);
    }
  }, [redirectTarget, history, isPublicTemplate]);

  if (isPublicTemplate && !token) {
    return (
      <Container component="div" disableGutters>
        <Typography variant="h4" className="my-1 page-header" align="center">
          Not allowed to access this page
        </Typography>
      </Container>
    );
  }

  if (isPublicTemplate && token && !userDetails?.id) {
    return (
      <Container component="div" disableGutters>
        <div className={classes.loadingContainer}>
          <CircularProgress style={{ height: '60px', width: '60px' }} />
        </div>
      </Container>
    );
  }

  return (
    <Grid container component={Container} style={{ display: 'flex', marginTop: '30px', textAlign: 'left', width: '100vw' }}>
      {/* GO BACK ICON */}
      {isPublicTemplate ? null : (
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12}>
              <div style={{ display: 'inline-block' }}>
                <Link to="/collections" className={classes.linkBack}>
                  <ChevronLeft className={classes.linkBackIcon} />
                  <span style={{ lineHeight: 1 }}>go back</span>
                </Link>
              </div>
            </Grid>
          </Grid>
        </Grid>
      )}
      {/* GO BACK ICON END */}

      {/* TITLE */}
      {template?.id ? (
        <Grid item xs={12}>
          <Typography className="page-header mb-0">{template.title}</Typography>
        </Grid>
      ) : null}
      {/* TITLE END */}

      {isNewTemplate ? (
        <Grid item xs={12}>
          <Typography className="page-header mb-0">Create New Collection</Typography>
          <Typography className={`mb-0 ${classes.headerDescription}`}>
            Please provide details for your collection and select the required resources and supplementary materials
          </Typography>
        </Grid>
      ) : null}

      {isLoading ? (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      ) : null}

      {/* FIELDS */}
      <Grid item xs={12} lg={6}>
        <div className={classes.fieldsContainer}>
          <EditFields
            ref={generalField}
            fields={generalFields}
            data={template}
            labelPosition="top"
            validate={TemplateModel.validateData}
            labelClass={classes.bold}
            useAutoFocus={false}
          />

          {isAllowedToEdit && !isLoading ? renderTagsField() : null}
          <div className="mt-0 mb-1">
            {(template.tags || []).map((tag, index) => (
              <Chip label={tag} onDelete={isAllowedToEdit && !isLoading ? () => handleRemoveTag(index) : null} style={{ margin: '4px 4px 4px 0' }} color="primary" />
            ))}
          </div>

          <EditFields
            ref={privacyField}
            fields={privacyFields}
            data={template}
            labelPosition="top"
            validate={TemplateModel.validateData}
            labelClass={classes.bold}
            useAutoFocus={false}
          />
        </div>
      </Grid>
      {/* FIELDS END */}

      {/* MATERIALS */}
      <Grid item xs={12} lg={6} className={classes.materialsContainer}>
        <div>
          <Typography className={`mb-0 ${classes.materialHeader}`}>Add Material</Typography>

          <TemplateMaterials
            template={template}
            onAddEducational={handleAddEducational}
            onRemoveEducationById={handleRemoveEducational}
            onSortEducational={handleSortEducational}
            onAddSupplementary={handleAddSupplementary}
            onRemoveSupplementaryById={handleRemoveSupplementary}
            onSortSupplementary={handleSortSupplementary}
            isAllowedToEdit={isAllowedToEdit && !isLoading}
            onOpenViewer={handleOpenResource}
          />
        </div>
      </Grid>
      {/* MATERIALS END */}

      {isAllowedToEdit ? (
        <Grid item xs={12}>
          <div className={classes.buttonsContainer}>
            <div style={{ flexGrow: 1 }} />

            {template?.id ? (
              <Button variant="outlined" color="secondary" className={classes.buttonAction} disabled={isLoading} onClick={handleDeleteTemplate}>
                Delete Collection
              </Button>
            ) : null}

            <Button variant="outlined" color="primary" className={classes.buttonAction} disabled={isLoading} onClick={handleCancelTemplate}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" className={classes.buttonAction} disabled={isLoading} onClick={handleSaveTemplate}>
              Save Collection
            </Button>
          </div>
        </Grid>
      ) : null}
    </Grid>
  );
};
